import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  Input,
  Select,
  Button,
  DatePicker,
  Space,
  Tag,
  message,
  Modal,
  Form,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { getBlacklistList, addOrEditBlacklist } from '@/api/lottoActivity';
import type { BlacklistListParams, BlacklistRecord } from '@/api/lottoActivity';
import locale from 'antd/es/date-picker/locale/zh_CN';
import debounce from 'lodash/debounce';

const { RangePicker } = DatePicker;
const { Option } = Select;

const statusMap = {
  0: { text: '已启用', color: 'green' },
  1: { text: '已禁用', color: 'red' },
};

const bannedTypeMap = {
  0: { text: '用户ID', color: 'blue' },
  1: { text: '请求IP', color: 'magenta' },
  2: { text: '设备ID', color: 'gold' },
};

interface Props {
  active: boolean;
}

const BlacklistTab: React.FC<Props> = ({ active }) => {
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useState<BlacklistListParams>({
    currentPage: 1,
    pageSize: 10,
  });
  const [data, setData] = useState<BlacklistRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingRecord, setEditingRecord] =
    useState<Partial<BlacklistRecord> | null>(null);

  const fetchData = useCallback(
    debounce(async () => {
      setLoading(true);
      try {
        const res = await getBlacklistList(params);
        if (res.success) {
          setData(res.data?.records || []);
          setTotal(res.data?.total || 0);
        } else {
          message.error(res.message || '获取数据失败');
        }
      } finally {
        setLoading(false);
      }
    }, 500),
    [params]
  );

  useEffect(() => {
    if (active) fetchData();
  }, [active]);

  useEffect(() => {
    fetchData();
  }, [params]);

  const handleSearch = (changed: Partial<BlacklistListParams>) => {
    setParams({
      ...params,
      currentPage: 1,
      ...changed,
    });
  };

  const handleAddOrEdit = useCallback(
    debounce(async (record: Partial<BlacklistRecord>) => {
      const targetStatus = record.status === 1 ? 0 : 1;
      const actionText = targetStatus === 1 ? '加入黑名单' : '移出黑名单';
      try {
        const res = await addOrEditBlacklist({
          id: record?.id || null,
          bannedType: record.bannedType!,
          userInfo:
            record.bannedType === 0
              ? record.userId!
              : record.bannedType === 1
                ? record.requestIp!
                : record.deviceId!,
          status: targetStatus,
        });

        if (res.success) {
          message.success(`${actionText}成功`);
          fetchData();
        } else {
          message.error(res.message || `${actionText}失败`);
        }
      } catch {
        message.error(`${actionText}异常`);
      }
    }, 500),
    [params]
  );

  const handleModalSubmit = useCallback(
    debounce(async () => {
      try {
        const values = await form.validateFields();
        const res = await addOrEditBlacklist({
          ...values,
          status: 1,
          id: editingRecord?.id || null,
        });
        if (res.success) {
          message.success('添加成功');
          setModalVisible(false);
          form.resetFields();
          setEditingRecord(null);
          fetchData();
        } else {
          message.error(res.message || '添加失败');
        }
      } catch {
        // 表单验证失败
      }
    }, 500),
    [editingRecord, form, fetchData]
  );

  const columns: ColumnsType<BlacklistRecord> = [
    { title: '请求时间', dataIndex: 'requestTime' },
    { title: '用户 ID', dataIndex: 'userId' },
    { title: 'SUPP ID', dataIndex: 'suppId' },
    { title: '请求IP', dataIndex: 'requestIp' },
    { title: '设备ID', dataIndex: 'deviceId' },
    {
      title: '封禁类型',
      dataIndex: 'bannedType',
      render: (val: number) => {
        const tag = bannedTypeMap[val];
        return <Tag color={tag?.color}>{tag?.text}</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (val: number) => {
        const tag = statusMap[val];
        return <Tag color={tag?.color}>{tag?.text}</Tag>;
      },
    },
    { title: '操作人', dataIndex: 'operatorName' },
    {
      title: '操作',
      render: (_, record) => {
        return record.status === 1 ? (
          <Button type="link" onClick={() => handleAddOrEdit(record)}>
            移出黑名单
          </Button>
        ) : (
          <Button
            type="link"
            onClick={() => {
              setEditingRecord(record);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            加入黑名单
          </Button>
        );
      },
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <RangePicker
          locale={locale}
          onChange={(dates, dateStrings) => {
            handleSearch({
              startTime: dateStrings[0],
              endTime: dateStrings[1],
            });
          }}
        />
        <Input
          placeholder="用户ID/SUPP ID/IP/设备ID"
          onChange={(e) => handleSearch({ keyword: e.target.value })}
          allowClear
        />
        <Select
          placeholder="状态"
          style={{ width: 120 }}
          onChange={(val) => handleSearch({ status: val })}
          allowClear
        >
          <Option value={0}>未封禁</Option>
          <Option value={1}>已禁用</Option>
        </Select>
        <Input
          placeholder="操作人"
          onChange={(e) =>
            handleSearch({ operatorName: Number(e.target.value) })
          }
          allowClear
        />
        <Button type="primary" onClick={fetchData}>
          查询
        </Button>
        <Button onClick={() => setParams({ currentPage: 1, pageSize: 10 })}>
          重置
        </Button>
        <Button
          type="dashed"
          onClick={() => {
            setEditingRecord(null);
            form.resetFields();
            setModalVisible(true);
          }}
        >
          + 添加黑名单
        </Button>
      </Space>
      <div style={{ marginBottom: 16 }}>所有记录，共 {total} 条</div>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={{
          total,
          current: params.currentPage,
          pageSize: params.pageSize,
          showSizeChanger: true,
          onChange: (current, pageSize) => {
            setParams({ ...params, currentPage: current, pageSize });
          },
        }}
      />

      <Modal
        title="添加黑名单"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingRecord(null);
          form.resetFields();
        }}
        onOk={handleModalSubmit}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="添加类型"
            name="bannedType"
            rules={[{ required: true, message: '请选择类型' }]}
          >
            <Select
              placeholder="请选择"
              onChange={(val) => {
                if (!editingRecord) return;
                const info =
                  val === 0
                    ? editingRecord.userId
                    : val === 1
                      ? editingRecord.requestIp
                      : editingRecord.deviceId;
                form.setFieldsValue({ userInfo: info || '' });
              }}
            >
              <Option value={0}>用户ID</Option>
              <Option value={1}>请求IP</Option>
              <Option value={2}>设备ID</Option>
            </Select>
          </Form.Item>
          <Form.Item
            label="用户信息"
            name="userInfo"
            rules={[{ required: true, message: '请输入信息' }]}
          >
            <Input placeholder="请输入用户ID / IP / 设备ID" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BlacklistTab;
